import React, { useEffect, useState } from "react";
import { withSidebar } from "../hoc/withSidebar";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import actions from "../../actions";
import ResponsiveDataGrid from "../custom/containers/ResponsiveDataGrid";
import CardList from "../custom/containers/cardList";
import { transactionSearchHeading } from "../../constants/TransactionSearchConstants.js";
import moment from "moment";
import { transactionStatusMap, transactionTypes, RoleType }  from "../../constants/sharedConstants.js";
import ERPSRecallRejectSearch, { getDateLimits, billerDetails } from "../custom/ERPSRecallRejectSearch";
import { getComponentAccess, formatDate, setFinancialInstitutionUIData } from "../../utils/helper";
import { ResponsiveBox, Typography } from "../primitives";

const RecallRejectHistory = (props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    history,
    location: { state },
  } = props;

  const [loading, setLoading] = useState(null);
  const [pageNumber, setPageNumber] = useState(0);
  const [errorMessage, setErrorMessage] = useState(null);

  const user = useSelector((state) => state.loginUserReducer.user);
  const [isBillerRole] = useState(user.role.role_type === RoleType.BILLER_ADMIN.type || user.role.role_type === RoleType.BILLER_USER.type);

  const componentAccess = state?.componentAccess
          || getComponentAccess( { function_type_code: "RRH", label_type_code: "TE" }, user );

  const [transactionStatusList] = useState(() => makeTransactionStatusList()); 

  function makeTransactionStatusList() {
    // filter will only keep Txn Statuses for F, W, X.
    const result = transactionStatusMap.entries().filter(e => "FWX".indexOf(e[0]) > -1)
                                  .map(e => ({value: e[0], label: t(e[1])})).toArray();
    result.unshift({value: " ", label: t("erps.common.label.all")});
    return result;
  }

  let dates = getDateLimits("RRH");
  
  const initialSearchValues = {
    transactionType: transactionTypes[0].value,
    paymentFromDate: dates.aMonthAgo,
    paymentToDate: dates.today,
    transactionStatus: transactionStatusList[0].value,
    selectedFiNumber: "0",
    searchBy: billerDetails[0].value,   // "billerId"
    billerId:  isBillerRole ? user.billerNonVolatileDto.biller_id : null, // internal biller id for Biller Role
    bmoBillerId: "",  // external biller id entered by the user
    billerName: "",
    billerCustomerNumber: "",
    fiCustomerName: "",
    fiTrackingNumber: ""
  };

  const rrhResponse = useSelector( (state) => state.transactionEnquiryReducer );

  const [searchValues, setSearchValues] = useState(initialSearchValues);
  const [actualCriteria, setActualCriteria] = useState(
    state?.fromDetailPage ? rrhResponse?.recallRejectHistoryDetails : { ...initialSearchValues, offset: 0, limit: 100}
  );

  const [transactionData, setTransactionData] = useState( state?.fromDetailPage && rrhResponse?.recallRejectHistoryData?.data || null );

  const [fiList, setFiList] = useState([]);
  const [fiMap, setFiMap] = useState(null);
  const fiReducer = useSelector( (state) => state.fiReducer );

  useEffect(() => {
    setFinancialInstitutionUIData(fiReducer?.financialInstitutionList, setFiMap, setFiList, null, user?.languageCode);
  }, [fiReducer.financialInstitutionList, user?.languageCode]);

  useEffect(() => {
    if (!(fiReducer?.financialInstitutionList?.length > 0))
      dispatch(actions.fiActions.getFinancialInstitutionList(t));

    if (state?.fromDetailPage) {
      // transfer into searchValues the attributes saved in rejectTransactionResponse.transactionForRejectDetails
      Object.keys(searchValues).filter(key => rrhResponse.recallRejectHistoryDetails[key])
            .forEach(key => searchValues[key] = rrhResponse?.recallRejectHistoryDetails[key]);
      setPageNumber(actualCriteria.offset / actualCriteria.limit);
      state.fromDetailPage = null;  // resetting the coming-from-detail-page flag
    } else if (state?.searchedValues) {  // WHAT DOES THIS DO ??
        setSearchValues(state?.searchedValues);
    } else {
      resetSearchDataInReducer();
    }
  }, []);

  function resetSearchDataInReducer() { // reset search resutls previously saved in the reducer
    dispatch({
      type: "GET_RECALL_REJECT_HISTORY_SUCCESS",
      data: { data: null, recallRejectHistoryDetails: null },
    });
  }

  useEffect(() => {
    if (loading === false && rrhResponse) {
      const responseData = rrhResponse.recallRejectHistoryData?.data;
      if (responseData?.length)
        responseData.forEach((r, idx) => r.seqNo = idx + 1 + actualCriteria.offset);
      setTransactionData(responseData);
    }
  }, [loading]);

  const paginate = (values) => {  // 'values' has fields startOffset and endOffset (e.g., for 1st Page: 0,25;  2nd Page: 25,50;  3rd: 50,75...)
    actualCriteria.offset = values.startOffset;
    if (actualCriteria.limit !== (values.endOffset - values.startOffset)) // if the user changed the number of rows per page
      actualCriteria.limit = values.endOffset - values.startOffset;

    loadResults(actualCriteria);
  };

  const loadResults = (queryParams) => {
    if (errorMessage)
      setErrorMessage(null);
    setLoading(true);

    dispatch(
      actions.transactionEnquiryActions.searchRecallRejectHistory(
        queryParams,
        { ...props, setLoading, setErrorMessage}
      )
    );
  };

  const handleReset = () => {
    setSearchValues({...initialSearchValues});
    setActualCriteria({ ...initialSearchValues, offset: 0, limit: actualCriteria.limit});
    setPageNumber(0);
    setErrorMessage(null);
    setTransactionData([]);

    resetSearchDataInReducer();
  };

  const handleSearch = (groomedValues) => {
    if (pageNumber)
      setPageNumber(0);

    if (groomedValues.paymentFromDate)
      groomedValues.paymentFromDate = formatDate(moment(groomedValues.paymentFromDate).format());
    if (groomedValues.paymentToDate)
      groomedValues.paymentToDate = formatDate(moment(groomedValues.paymentToDate).format());
    if (!groomedValues.transactionStatus)
      groomedValues.transactionStatus = transactionStatusList.slice(1).map(ts => ts.value).join(",");
    groomedValues.limit = actualCriteria.limit;
    groomedValues.offset = 0;
    setActualCriteria({...groomedValues});

    if (!isBillerRole) {
      if (groomedValues.searchBy === billerDetails[0].value)  // "billerId"
        delete groomedValues["billerName"];
      else if (groomedValues.searchBy === billerDetails[1].value)  //  "billerName"
        delete groomedValues["bmoBillerId"];
    }

    loadResults(groomedValues);
  };

  function isShowFI() {
    // FI column is shown for Biller Admin/User;  but for FI Admin/User only for Rejects (i.e. transaction type is "RJ")
    return isBillerRole || actualCriteria.transactionType == "RJ";
  }

  return (
    <div role="main">
      <ResponsiveBox sx={{ ml: 3}}>
        <ERPSRecallRejectSearch 
            pageType="RRH"
            pageTitle={t("erps.recallsrejectshistory.page.title")}
            pageDescription={t("erps.transactionEnquiry.page.description")}
            searchValues={searchValues}
            setSearchValues={setSearchValues}
            dates={dates}
            loading={loading}
            errorMessage={errorMessage}
            componentAccess={componentAccess}
            handleReset={handleReset}
            handleSearch={handleSearch}
            financialInstitutionList={fiList}
            transactionStatusList={transactionStatusList}
          />

        {transactionData !== null && transactionData !== undefined
         && Array.isArray(transactionData) && transactionData.length > 0 ? (
          <>
            <ResponsiveBox className="manageUsers_table_visible">
              <ResponsiveDataGrid
                data={{
                  rows: transactionData,
                  columns: transactionSearchHeading("RRH", isShowFI() ? fiMap : null, "/recall-reject-history/recall-reject-details"),
                  count: rrhResponse?.recallRejectHistoryData?.count || 0,
                }}
                sumOfColumnWidth="true"
                onAnyPaginationAction={paginate}
                initialLoading={loading}
                pageNumber={pageNumber}
                setPage={setPageNumber}
                rowPerPage={actualCriteria.limit}
                tableLabelText={"_Page Title_"}
                //tableLabelText={t("erps.transactionEnquiry.page.title")}
                hideFooterPagination={false}
                pageName={"transaction-enquiry-rrh"}
              />
            </ResponsiveBox>
            <ResponsiveBox className="manageUsers_card_visible">
              <CardList
                data={{
                  rows: transactionData,
                  columns: transactionSearchHeading("RRH", isShowFI() ? fiMap : null, "/recall-reject-history/recall-reject-details"),
                  count: rrhResponse?.recallRejectHistoryData?.count || 0,
                }}
                onAnyPaginationAction={paginate}
                hideFooterPagination={false}
                pageName={"recall-reject-history"}
                pageNumber={pageNumber}
                setPage={setPageNumber}
                rowPerPage={actualCriteria.limit}
              />
            </ResponsiveBox>
          </>
        ) : (
          <ResponsiveBox mt={1}>
            <Typography variant="body1">
                { (transactionData?.length === 0)
                    ? (`0 ${t("erps.transactionEnquiry.page.resultfound")}`)
                    : (t("erps.availabledocuments.searchuser.notstarted", "Search not started"))
                }
            </Typography>
          </ResponsiveBox>
        )}
      </ResponsiveBox>
    </div>
  );
};

export default withSidebar(RecallRejectHistory);
